import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import ModalBase from "../../../../components/ui/ModalBase";
import colors from "../../../../shared/constants/color";
import axios from "axios";
import { SERVICEURL } from "../../../../appconfig";
import { useToastContext } from "../../../../context/ToastContextProvider";
import {
  getPhoneDetail,
  updatePhoneInfo,
} from "../../../../api/phoneApi";
import { CommunicationParameterGuids } from "../../../../constants/communicationParameterGuids";

interface AddPhoneModalProps {
  open: boolean;
  memberId: string | number;
  phoneId?: string | null;
  onClose: () => void;
}

export interface PhoneInfoListDTO {
  PhoneID: string;
  MemberID: string;
  CommunicationType: string;
  PhoneNo: string;
}

const initialFormState = {
  phoneType: "",
  countryCode: "90",
  phone: "",
};

/**
 * Telefon tipi olarak yalnizca IS ve OZEL secilebilir.
 * Upstream c3d23e5 bunu `new Set([2, 3])` legacy int kumesi olarak tutuyordu;
 * bizde CommunicationParameter id'leri Guid oldugu icin o kume hicbir seyle
 * eslesmez ve liste TAMAMEN bosalirdi.
 */
const ALLOWED_PHONE_TYPE_IDS = new Set<string>([
  CommunicationParameterGuids.work, // 2 İŞ
  CommunicationParameterGuids.private, // 3 ÖZEL
]);

const isAllowedPhoneType = (value: unknown): boolean =>
  !!value && ALLOWED_PHONE_TYPE_IDS.has(String(value).toLowerCase());

/** Format: (XXX) XXX XX XX — ilk 3 hane parantez icinde */
const formatPhoneNumber = (value: string) => {
  let phoneNumber = value.replace(/\D/g, "");

  if (!phoneNumber) return "";

  if (phoneNumber.startsWith("0")) {
    phoneNumber = phoneNumber.slice(1);
  }

  phoneNumber = phoneNumber.slice(0, 10);

  if (phoneNumber.length <= 3) {
    return `(${phoneNumber}`;
  }
  if (phoneNumber.length <= 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  if (phoneNumber.length <= 8) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6, 8)} ${phoneNumber.slice(8, 10)}`;
};

/** Validasyon ve payload maskesiz calisir. */
const getPhoneDigits = (value: string) =>
  value.replace(/\D/g, "").slice(0, 10);

const AddPhoneModal: React.FC<
  AddPhoneModalProps & { onSuccess?: (item: PhoneInfoListDTO) => void }
> = ({
  open,
  memberId,
  phoneId = null,
  onClose,
  onSuccess,
}) => {
  const isEditMode = !!phoneId;
  const [form, setForm] = useState<typeof initialFormState>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneTypeOptions, setPhoneTypeOptions] = useState<any[]>([]);
  const { showToast } = useToastContext();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const resetState = () => {
    setForm(initialFormState);
    setErrors({});
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined;

        // Telefon tiplerini yükle
        const response = await axios.get(
          `${SERVICEURL}api/Data/GetCommunicationParameterList`,
          { headers }
        );

        const phoneTypeData = response.data?.data || [];
        const filteredPhoneTypes = Array.isArray(phoneTypeData)
          ? phoneTypeData.filter((type: { id: string }) =>
              isAllowedPhoneType(type.id),
            )
          : [];
        setPhoneTypeOptions(filteredPhoneTypes);

        // Düzenleme modundaysa telefon verisini yükle
        if (phoneId) {
          const data = await getPhoneDetail(phoneId);

          // Telefon numarasını country code ve phone olarak ayır
          const fullPhone = data.phoneNo || "";
          let countryCode = "90";
          let phone = fullPhone;
          
          // Eğer telefon numarası "-" içeriyorsa ayır
          if (fullPhone.includes("-")) {
            const parts = fullPhone.split("-");
            countryCode = parts[0].replace(/\D/g, "");
            // parts[1] yerine kalanin tamami: numarada "-" varsa kirpilmasin.
            phone = parts.slice(1).join("-");
          } else {
            // "-" yoksa, ilk 2-3 karakter country code olabilir
            const cleaned = fullPhone.replace(/\D/g, "");
            if (cleaned.length > 10) {
              countryCode = cleaned.substring(0, cleaned.length - 10);
              phone = cleaned.substring(cleaned.length - 10);
            } else {
              phone = cleaned;
            }
          }

          const loadedTypeId =
            data.communicationParameterId?.toString() || "";
          const phoneType = isAllowedPhoneType(loadedTypeId)
            ? loadedTypeId
            : "";

          setForm({
            phoneType,
            countryCode: countryCode,
            phone: formatPhoneNumber(phone),
          });
          setErrors({});
        } else {
          resetState();
        }
      } catch (error) {
        console.error("Data loading error:", error);
        if (phoneId) {
          showToast("Telefon bilgisi yüklenirken bir hata oluştu.", "error");
        }
      }
    };

    fetchData();
  }, [open, phoneId]);

  const validateField = (name: string, value: string) => {
    setErrors((prevErrors) => {
      const newErrors: Record<string, string> = { ...prevErrors };

      if (name === "phoneType") {
        if (!isAllowedPhoneType(value)) {
          newErrors.phoneType = "Telefon tipi seçimi zorunludur.";
        } else {
          delete newErrors.phoneType;
        }
      }

      if (name === "countryCode") {
        if (!value) {
          newErrors.countryCode = "Ülke kodu girilmesi zorunludur.";
        } else if (value.length === 1) {
          newErrors.countryCode = "Ülke kodu 2 ya da 3 karakterden oluşmalıdır";
        } else if (value.length > 3) {
          newErrors.countryCode = "Ülke kodu en fazla 3 karakter olabilir";
        } else {
          delete newErrors.countryCode;
        }
      }

      if (name === "phone") {
        // Maske icerdigi icin uzunluk kontrolu HANELER uzerinden yapilir.
        const digits = getPhoneDigits(value);
        if (!digits) {
          newErrors.phone = "Telefon numarası girilmesi zorunludur.";
        } else if (digits.length < 10) {
          newErrors.phone = "Telefon bilgisi 10 karakterden oluşabilir";
        } else if (digits.length > 10) {
          newErrors.phone = "Telefon numarası en fazla 10 karakter olabilir";
        } else {
          delete newErrors.phone;
        }
      }

      return newErrors;
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const formatted = formatPhoneNumber(value);
      setForm((prev) => ({ ...prev, phone: formatted }));
      validateField(name, formatted);
      return;
    }

    if (name === "countryCode") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 3);
      setForm((prev) => ({ ...prev, countryCode: digitsOnly }));
      validateField(name, digitsOnly);
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const phoneDigits = getPhoneDigits(form.phone);

    if (!isAllowedPhoneType(form.phoneType)) {
      newErrors.phoneType = "Telefon tipi seçimi zorunludur.";
    }
    
    if (!form.countryCode) {
      newErrors.countryCode = "Ülke kodu girilmesi zorunludur.";
    } else if (form.countryCode.length === 1) {
      newErrors.countryCode = "Ülke kodu 2 ya da 3 karakterden oluşmalıdır";
    } else if (form.countryCode.length > 3) {
      newErrors.countryCode = "Ülke kodu en fazla 3 karakter olabilir";
    }
    
    if (!phoneDigits) {
      newErrors.phone = "Telefon numarası girilmesi zorunludur.";
    } else if (phoneDigits.length < 10) {
      newErrors.phone = "Telefon bilgisi 10 karakterden oluşabilir";
    } else if (phoneDigits.length > 10) {
      newErrors.phone = "Telefon numarası en fazla 10 karakter olabilir";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = () => {
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    // Onay diyalogundan gelirken de validasyon calissin.
    if (!validate()) {
      setConfirmOpen(false);
      return;
    }

    setConfirmOpen(false);

    try {
      setIsSubmitting(true);

      // Payload'a maske DEGIL haneler gider.
      const phoneDigits = getPhoneDigits(form.phone);

      if (isEditMode && phoneId) {
        // Düzenleme modu - PUT isteği
        const updatePayload = {
          phoneId: phoneId,
          phoneNo: `${form.countryCode}-${phoneDigits}`,
          communicationParameterId: form.phoneType || undefined,
        };

        await updatePhoneInfo(updatePayload);
        showToast("Telefon bilgisi başarıyla güncellendi.", "success");
        if (onSuccess) {
          onSuccess({} as PhoneInfoListDTO);
        }
        handleClose();
      } else {
        // Ekleme modu - POST isteği
        const token = localStorage.getItem("token");
        const currentUserId = localStorage.getItem("memberId");

        const payload = {
          PhoneNo: phoneDigits,
          CountryCode: form.countryCode,
          CommunicationParameterID: form.phoneType || null,
          EnteringMemberID: null,
        };

      const response = await axios.post(
        `${SERVICEURL}api/Candidates/AddPhoneInfo/${memberId}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const responseData = response.data?.data || response.data;
      
      const apiData: PhoneInfoListDTO = {
        PhoneID: responseData?.phoneID || responseData?.PhoneID || "",
        MemberID: responseData?.memberID || responseData?.MemberID || "",
        CommunicationType: responseData?.communicationType || responseData?.CommunicationType || "",
        PhoneNo: responseData?.phoneNo || responseData?.PhoneNo || "",
      };

      if (apiData.CommunicationType && apiData.PhoneNo && onSuccess) {
        onSuccess(apiData);
      } else {
        console.warn("API response is invalid or missing required fields:", apiData, "Original:", responseData);
      }

        showToast("Telefon bilgisi başarıyla eklendi.", "success");
        handleClose();
      }
    } catch (error: any) {
      console.error("Phone save error:", error);
      const errorMessage = isEditMode
        ? error.response?.data?.message || "Telefon bilgisi güncellenirken bir hata oluştu."
        : error.response?.data?.message || "Telefon eklenirken bir hata oluştu.";
      showToast(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <ModalBase
      open={open}
      onClose={handleClose}
      title={isEditMode ? "Telefon Düzenle" : "Yeni Telefon Ekle"}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ borderRadius: 2, textTransform: "none", minWidth: 80 }}
          >
            Kapat
          </Button>
          <Button
            variant="contained"
            sx={{ borderRadius: 2, minWidth: 80 }}
            disabled={isSubmitting}
            onClick={handleSaveClick}
          >
            {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </>
      }
      maxWidth="md"
      minWidth={700}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 2,
          width: "100%",
        }}
      >
        <TextField
          select
          fullWidth
          label="Telefon Tipi*"
          name="phoneType"
          value={form.phoneType}
          onChange={handleChange}
          size="small"
          error={!!errors.phoneType}
          helperText={errors.phoneType}
        >
          <MenuItem value="">Seçiniz</MenuItem>
          {phoneTypeOptions.map((type) => (
            <MenuItem key={type.id} value={type.id.toString()}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label="Ülke Kodu*"
          name="countryCode"
          value={form.countryCode}
          onChange={handleChange}
          size="small"
          error={!!errors.countryCode}
          helperText={errors.countryCode}
          inputProps={{
            // handleChange zaten rakama indirip 3 haneye kirpiyor; onInput ile
            // e.target.value'yu elle yazmak React'in denetimli degeriyle catisiyordu.
            maxLength: 3,
            inputMode: "numeric",
          }}
        />

        <TextField
          fullWidth
          label="Telefon*"
          name="phone"
          value={form.phone}
          onChange={handleChange}
          size="small"
          error={!!errors.phone}
          helperText={errors.phone}
          placeholder="(5XX) XXX XX XX"
          inputProps={{
            // Maske 14 karakter uretiyor ((5XX) XXX XX XX); maxLength 10 kalirsa
            // alan 10. karakterde kilitlenir ve numara tamamlanamaz.
            // pattern="[0-9]*" ve rakam disini silen onInput da maskeye karsi
            // calisiyordu, ikisi de kaldirildi — sinirlama artik formatPhoneNumber
            // ve getPhoneDigits'te.
            maxLength: 15,
            inputMode: "numeric",
          }}
        />
      </Box>
    </ModalBase>
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
      <DialogTitle>
        {isEditMode ? "Telefon Güncellensin mi?" : "Telefon Eklensin mi?"}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {isEditMode 
            ? "Telefon güncellemek istediğinize emin misiniz?" 
            : "Telefon eklemek istediğinize emin misiniz?"}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>
          Hayır
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          sx={{ textTransform: "none", background: colors.blue.B800 }}
        >
          Evet
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddPhoneModal;

